import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-proto';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-proto';
import { metrics as metricsApi, trace, SpanStatusCode } from '@opentelemetry/api';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { BatchLogRecordProcessor, LoggerProvider } from '@opentelemetry/sdk-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-proto';
import { logs } from '@opentelemetry/api-logs';
import { UAParser } from 'ua-parser-js';

const DEPLOYMENT_ENVIRONMENT = 'deployment.environment';
const OS_NAME = 'os.name';
const OS_VERSION = 'os.version';
const BROWSER_FAMILY = 'browser.family';
const BROWSER_VERSION = 'browser.version';
const BROWSER_IS_WEBVIEW = 'browser.is_webview';

function detectIsWebView(): boolean {
  try {
    const w = window as any;
    const ua = navigator.userAgent || '';

    const isAndroidWV = /Android/i.test(ua) && (/(;\s?wv;)/i.test(ua) || /Version\/\d+\.\d+\s+Chrome\//.test(ua));
    const isiOS = /(iPhone|iPad|iPod)/i.test(ua);
    const isiOSWV = isiOS && /AppleWebKit/i.test(ua) && !/Safari/i.test(ua);

    const hasWKHandlers = !!w.webkit?.messageHandlers; // WKWebView bridge
    const hasRN = typeof w.ReactNativeWebView !== 'undefined';
    const hasHybrid = typeof w.cordova !== 'undefined' || typeof w.Capacitor !== 'undefined';
    const inApp = /(FBAN|FBAV|Instagram|Line|WeChat|Twitter|Snapchat|LinkedIn|GSA)/i.test(ua);

    return isAndroidWV || isiOSWV || hasWKHandlers || hasRN || hasHybrid || inApp;
  } catch {
    return false;
  }
}
// Lightweight, idempotent init so it can be imported early in main.ts
let initialized = false;
let tracerProviderRef: WebTracerProvider | undefined;
let meterProviderRef: MeterProvider | undefined;
let metricReaderRef: PeriodicExportingMetricReader | undefined;

function toAbsoluteUrl(u?: string): string | undefined {
  if (!u) return undefined;
  try {
    // If already absolute, this returns as-is; if relative, it uses current origin
    return new URL(u, window.location.origin).toString();
  } catch {
    return u;
  }
}

export function initOtel(config: { serviceName: string; otlpTracesUrl: string; environment: string; otlpMetricsUrl: string; otlpLogsUrl: string }): void {
  if (initialized) return;
  initialized = true;

  const otlpTracesUrl = toAbsoluteUrl(config.otlpTracesUrl)!;
  const otlpMetricsUrl = toAbsoluteUrl(config.otlpMetricsUrl);
  const otlpLogsUrl = toAbsoluteUrl(config.otlpLogsUrl);

  // Derive device/browser once and attach via Resource so both traces and metrics carry them
  const { osName, osVersion, browserFamily, browserVersion } = parseUserAgent();
  const isWebView = detectIsWebView();

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: config.serviceName,
    [DEPLOYMENT_ENVIRONMENT]: config.environment,
    [OS_NAME]: osName,
    [OS_VERSION]: osVersion,
    [BROWSER_FAMILY]: browserFamily,
    [BROWSER_VERSION]: browserVersion,
    [BROWSER_IS_WEBVIEW]: isWebView
  });

  // Only initialize log exporter if URL is provided
  if (otlpLogsUrl) {
    const loggerProvider = new LoggerProvider({
      resource: resource,
      processors: [new BatchLogRecordProcessor({ exporter: new OTLPLogExporter({ url: otlpLogsUrl }) })]
    });
    logs.setGlobalLoggerProvider(loggerProvider);
  }

  // Only initialize trace exporter if URL is provided
  const provider = new WebTracerProvider({
    resource,
    spanProcessors: otlpTracesUrl ? [new BatchSpanProcessor(new OTLPTraceExporter({ url: otlpTracesUrl }))] : []
  });

  // Use Zone.js context manager for better Angular compatibility
  provider.register({ contextManager: new ZoneContextManager() });
  tracerProviderRef = provider;

  registerInstrumentations({
    instrumentations: [
      getWebAutoInstrumentations({
        '@opentelemetry/instrumentation-document-load': { enabled: true },
        '@opentelemetry/instrumentation-user-interaction': { enabled: true },
        '@opentelemetry/instrumentation-xml-http-request': {
          enabled: true,
          applyCustomAttributesOnSpan: (span, xhr) => {
            const status = (xhr as XMLHttpRequest).status;
            if (status) span.setAttribute('http.response.status_code', status);
            if (status >= 400) {
              span.setStatus({ code: SpanStatusCode.ERROR });
            }
          }
        },
        '@opentelemetry/instrumentation-fetch': {
          enabled: true,
          applyCustomAttributesOnSpan: (span, _request, result) => {
            try {
              if (result instanceof Response) {
                span.setAttribute('http.response.status_code', result.status);
                if (result.status >= 400) {
                  span.setStatus({ code: SpanStatusCode.ERROR });
                }
              }
            } catch {
              /* no-op */
            }
          }
        }
      })
    ]
  });

  // Capture unhandled browser errors and rejections as spans + a metric
  const tracer = trace.getTracer(config.serviceName);
  const meter = metricsApi.getMeter(config.serviceName);
  const errorCounter = meter.createCounter('browser_errors_total', {
    description: 'Total number of unhandled browser errors'
  });

  function reportUnhandled(error: unknown, context: string, extra?: Record<string, unknown>) {
    const errObj = error instanceof Error ? error : new Error(String(error));
    tracer.startActiveSpan('browser.unhandled_error', (span) => {
      span.recordException(errObj);
      span.setAttributes({
        'exception.type': errObj.name,
        'exception.message': errObj.message,
        'exception.stacktrace': errObj.stack ?? '',
        'exception.escaped': true,
        'error.context': context,
        ...extra
      });
      span.setStatus({ code: SpanStatusCode.ERROR, message: errObj.message });
      span.end();
    });
    errorCounter.add(1, {
      environment: config.environment,
      'error.type': errObj.name,
      'error.context': context
    });
  }

  window.addEventListener('error', (ev: ErrorEvent) => {
    reportUnhandled(ev.error ?? ev.message, 'window.error', {
      'error.filename': ev.filename,
      'error.lineno': ev.lineno,
      'error.colno': ev.colno
    });
  });

  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    reportUnhandled(ev.reason, 'window.unhandledrejection');
  });

  // Optional metrics via OTLP protobuf if URL is provided
  if (otlpMetricsUrl) {
    const metricReader = new PeriodicExportingMetricReader({
      exporter: new OTLPMetricExporter({ url: otlpMetricsUrl }),
      exportIntervalMillis: 1000000
    });
    const meterProvider = new MeterProvider({ resource, readers: [metricReader] });
    metricsApi.setGlobalMeterProvider(meterProvider);
    meterProviderRef = meterProvider;
    metricReaderRef = metricReader;

    // Opportunistically flush metrics when page is hidden or being unloaded
    const scheduleFlush = () => {
      // fire-and-forget; exporter will use keepalive/beacon when possible
      void flushMetrics();
    };
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) scheduleFlush();
    });
    window.addEventListener('pagehide', scheduleFlush);
    window.addEventListener('beforeunload', scheduleFlush);
  }
}

// Manually flush metrics (and optionally traces) on demand
export async function flushMetrics(): Promise<void> {
  try {
    if (metricReaderRef) {
      await metricReaderRef.forceFlush();
    } else if (meterProviderRef) {
      await meterProviderRef.forceFlush();
    }
  } catch {
    // no-op
  }
}

export async function flushTraces(): Promise<void> {
  try {
    if (tracerProviderRef) await tracerProviderRef.forceFlush();
  } catch {
    // no-op
  }
}

function parseUserAgent(): { osName: string; osVersion: string; browserFamily: string; browserVersion: string } {
  try {
    const { os, browser } = UAParser();
    return {
      osName: os.name ?? 'unknown',
      osVersion: os.version ?? 'unknown', //it's not accurate due to security reasons
      browserFamily: browser.name ?? 'Other',
      browserVersion: browser.version ?? 'unknown'
    };
  } catch {
    return { osName: 'unknown', osVersion: 'unknown', browserFamily: 'Other', browserVersion: 'unknown' };
  }
}
